import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterLinkWithHref,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Channel } from '../../../models/channel.model';
import { Group, GroupType } from '../../../models/group.model';
import { SchoolMemberType } from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { Organization } from '../../../models/organization.model';
import { UserModel } from '../../../models/user.model';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { SchoolRolesService } from '../../../services/school-roles.service';
import { OrganizationRequestService } from '../../../services/organization-request.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import * as ManagementSelectors from '../../../state/selectors/management.selectors';
import { UserDropdownComponent } from '../dropdowns/user-dropdown/user-dropdown.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  templateUrl: './sidebar.component.html',
  imports: [
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    RouterLinkWithHref,
    NgxSkeletonLoaderModule,
    UserDropdownComponent,
    InitialsPipe,
  ],
})
export class SidebarComponent implements OnDestroy {
  private breakpointObserver = inject(BreakpointObserver);
  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  logger: NGXLogger = inject(NGXLogger);
  store: Store<AppState> = inject(Store<AppState>);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private orgRequestService = inject(OrganizationRequestService);
  // Platform admins (platformAdmin custom claim) see the organization requests review link
  isPlatformAdmin = false;

  user$: Observable<UserModel | null> = new Observable();

  groups$: Observable<Group[]> = new Observable();
  loadingGroups$: Observable<boolean> = new Observable();

  organizations$: Observable<Organization[]> = new Observable();
  loadingOrganizations$: Observable<boolean> = new Observable();

  channel$: Observable<Channel | null> = new Observable();
  loadingChannel$: Observable<boolean> = new Observable();

  membershipStatus = MembershipStatus;
  groupType = GroupType;
  schoolMemberType = SchoolMemberType;

  isOpen: boolean = false;
  expandedGroupId: string | null = null;
  expandedOrgId: string | null = null;

  exit$: Subject<void> = new Subject<void>();

  groupId: string | null = null;
  // academicYear: number | undefined = undefined;

  isMobile$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
    .pipe(
      map((result) => result.matches),
      shareReplay(),
    );

  private params$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith(null),
    map(() => {
      let route: ActivatedRouteSnapshot | null =
        this.router.routerState.snapshot.root;
      let id: string | null = null;

      while (route) {
        if (route.paramMap.has('id')) {
          id = route.paramMap.get('id');
        }
        if (route.paramMap.has('orgId')) {
          const orgId = route.paramMap.get('orgId');
          if (orgId) {
            this.expandedOrgId = orgId;
          }
        }
        route = route.firstChild;
      }
      return id;
    }),
    debounceTime(20),
    distinctUntilChanged(),
    tap((id) => {
      this.groupId = id;
      this.expandedGroupId = id ? id : null;
      this.logger.debug(`SidebarComponent :: ID update: ${id}`);
    }),
    shareReplay(1),
  );

  myRoles$: Observable<any> = this.params$.pipe(
    switchMap((id) => {
      return this.store.select(groupSelector.getMyRolesInGroup(id));
    }),
    shareReplay(1),
  );

  routerActiveClass = 'bg-blue-50 border-l-4 border-secondary-500';
  routerLinkActiveOptions = { exact: false };

  constructor() {
    this.params$.subscribe();

    this.user$ = this.store.select((state: AppState) => state.user.user);
    this.user$.pipe(takeUntil(this.exit$)).subscribe(async (u) => {
      this.isPlatformAdmin = u ? await this.orgRequestService.isPlatformAdmin() : false;
    });

    this.groups$ = this.store.select(groupSelector.getAllGroups);
    this.loadingGroups$ = this.store.select((state: AppState) => state.groups.loading);

    this.organizations$ = this.store.select(ManagementSelectors.selectMyOrganizations);
    this.loadingOrganizations$ = this.store.select(ManagementSelectors.selectLoadingMyOrganizations);

    this.channel$ = this.store.select((state: AppState) => state.channel.channel);
    this.loadingChannel$ = this.store.select((state: AppState) => state.channel.loading);
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onGroupClick(item: Group, event?: MouseEvent) {
    // Toggle the submenu
    const expanding = this.expandedGroupId !== item.id;
    this.expandedGroupId = expanding ? item.id : null;

    // Optional: Navigate to the group's default landing page
    this.router.navigate(this.getGroupRoute(item));

    // Close the mobile menu if we are on mobile
    this.closeMobileMenuIfMobile();

    if (expanding && event) {
      const target = event.currentTarget as HTMLElement;
      setTimeout(() => {
        const groupLi = target.closest('li');
        const scrollContainer = target.closest('.overflow-y-auto') as HTMLElement;
        if (groupLi && scrollContainer) {
          const subMenuUl = groupLi.querySelector('ul');
          const subItems = Array.from(subMenuUl?.children || []) as HTMLElement[];
          const totalItems = subItems.length;

          let selectedIndex = subItems.findIndex(
            (li) => li.classList.contains('bg-blue-50') || li.querySelector('.bg-blue-50')
          );
          if (selectedIndex === -1) {
            selectedIndex = 0;
          }

          const isFirstHalf = totalItems > 0 && selectedIndex < totalItems / 2;

          const myCommunitiesH6 = scrollContainer.querySelector('h6') as HTMLElement;
          const headerOffset = myCommunitiesH6 ? myCommunitiesH6.offsetHeight : 32;

          let targetScrollTop = 0;
          if (isFirstHalf || totalItems === 0) {
            targetScrollTop = Math.max(0, groupLi.offsetTop - headerOffset);
          } else {
            const groupBottom = groupLi.offsetTop + groupLi.offsetHeight;
            targetScrollTop = Math.max(
              groupLi.offsetTop - headerOffset,
              groupBottom - scrollContainer.clientHeight
            );
            targetScrollTop = Math.max(0, targetScrollTop);
          }

          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });
        }
      }, 150);
    }
  }

  private closeMobileMenuIfMobile() {
    if (window.innerWidth < 768) {
      this.isOpen = false;
    }
  }

  onSubItemClick() {
    if (window.innerWidth < 768) {
      this.isOpen = false;
    }
  }

  private getGroupRoute(item: Group): any[] {
    return item.groupType != GroupType.school
      ? ['../groups', item?.id]
      : item.memberType.length == 1 &&
          (item.memberType as SchoolMemberType[]).includes(
            SchoolMemberType.student,
          )
        ? ['../school', item.id, 'students', item.memberId]
        : (item.memberType as SchoolMemberType[]).some((type) =>
              [SchoolMemberType.president, SchoolMemberType.principal].includes(
                type,
              ),
            )
          ? ['../school', item?.id, 'dashboard']
          : (item.memberType as SchoolMemberType[]).includes(
                SchoolMemberType.teacher,
              )
            ? ['../school', item.id, 'faculty', item.memberId]
            : item.memberType.length == 1 &&
                (item.memberType as SchoolMemberType[]).includes(
                  SchoolMemberType.parent,
                )
              ? ['../school', item.id, 'parents', item.memberId]
              : ['../school', item.id, 'details'];
  }

  toggleGroup(groupId: string, event: Event) {
    // Prevent the click from triggering parent navigation immediately if intended
    event.stopPropagation();

    if (this.expandedGroupId === groupId) {
      this.expandedGroupId = null; // Collapse if clicking the same one
    } else {
      this.expandedGroupId = groupId; // Expand the new one
    }
  }

  onOrgClick(org: Organization, event?: MouseEvent): void {
    const expanding = this.expandedOrgId !== org.id;
    this.expandedOrgId = expanding ? org.id : null;
    this.router.navigate(['/management', org.id]);
    this.closeMobileMenuIfMobile();
  }

  toggleOrg(orgId: string, event: Event): void {
    event.stopPropagation();
    this.expandedOrgId = this.expandedOrgId === orgId ? null : orgId;
  }

  toggleMobileMenu() {
    this.isOpen = !this.isOpen;
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }
}
